<template>
  <editor class="editor relative p-3 bg-grey-lighter border" :class="{ 'my-6': !withoutMargin }" :extensions="extensions" @update="onUpdate" ref="editor">
    <div slot="menububble" @mousedown.prevent slot-scope="{ nodes, marks, focus }" class="absolute z-10 bg-grey-darker text-white rounded-full -mx-1 px-6 py-1 mb-1 invisible flex items-baseline font-serif">
      <template v-if="marks && !linkMenuIsActive">
        <div class="px-1 flex font-normal" v-if="options.indexOf('heading') !== -1">
          <button
            class="rounded text-white py-1 px-1 mx-1 text-sm"
            :class="{ 'bg-black': nodes.heading.active({ level: 1 }) }"
            @click="nodes.heading.command({ level: 1 })"
            >
            H1
          </button>
          <button
            class="rounded text-white py-1 px-1 mx-1 text-sm"
            :class="{ 'bg-black': nodes.heading.active({ level: 2 }) }"
            @click="nodes.heading.command({ level: 2 })"
            >
            H2
          </button>
          <button
            class="rounded text-white py-1 px-1 mx-1 text-sm"
            :class="{ 'bg-black': nodes.heading.active({ level: 3 }) }"
            @click="nodes.heading.command({ level: 3 })"
            >
            H3
          </button>
        </div>
        <div class="px-1 flex">
          <button
            class="rounded text-white py-1 px-2 mx-1"
            :class="{ 'bg-black': marks.bold.active() }"
            @click="marks.bold.command"
            v-if="options.indexOf('bold') !== -1"
            >
            <i class="fas fa-bold" />
          </button>
          <button
            class="rounded text-white py-1 px-2 mx-1"
            :class="{ 'bg-black': marks.italic.active() }"
            @click="marks.italic.command"
            v-if="options.indexOf('italic') !== -1"
            >
            <i class="fas fa-italic" />
          </button>
          <button
            class="rounded text-white py-1 px-2 mx-1"
            :class="{ 'bg-black': marks.underline.active() }"
            @click="marks.underline.command"
            v-if="options.indexOf('underline') !== -1"
            >
            <i class="fas fa-underline" />
          </button>
        </div>
        <div class="px-1 flex">
          <button
            class="rounded text-white py-1 px-2 mx-1"
            :class="{ 'bg-black': nodes.bullet_list.active() }"
            @click="nodes.bullet_list.command"
            v-if="options.indexOf('bullet-list') !== -1"
            >
            <i class="fas fa-list-ul" />
          </button>
          <button
            class="rounded text-white py-1 px-2 mx-1"
            :class="{ 'bg-black': nodes.ordered_list.active() }"
            @click="nodes.ordered_list.command"
            v-if="options.indexOf('ordered-list') !== -1"
            >
            <i class="fas fa-list-ol" />
          </button>
        </div>
        <!-- <div class="px-1 flex">
          <button
            class="rounded text-white py-1 px-2 mx-1"
            @click="nodes.image.command"
            v-if="options.indexOf('image') !== -1"
            >
            <i class="fas fa-image" />
          </button>
        </div> -->
      </template>
      <form v-if="linkMenuIsActive" @submit.prevent="setLinkUrl(marks.link.command, linkUrl)" class="flex">
        <input
          ref="linkInput"
          v-model="linkUrl"
          type="text"
          placeholder="https://"
          class="block appearance-none py-1 bg-transparent text-white leading-tight"
          @keydown.esc="hideLinkMenu"
        >
        <button
          type="button"
          class="rounded text-white py-1 px-2"
          @click="setLinkUrl(marks.link.command, null)"
          >
          <i class="fas fa-times fa-xs" />
        </button>
      </form>
    </div>
    <div slot="content" slot-scope="props"></div>
  </editor>
</template>

<script>
import { Editor } from 'tiptap'
import {
  HardBreakNode,
  LinkMark,
  BoldMark,
  ItalicMark,
  UnderlineMark,
  ImageNode,
  HeadingNode,
  ListItemNode,
  BulletListNode,
  OrderedListNode,
  HistoryExtension,
  PlaceholderExtension
} from 'tiptap-extensions'
import { EventBus } from '@/EventBus'

export default {
  name: 'pl-editor',
  components: { Editor },
  props: {
    withoutMargin: {
      type: Boolean,
      default: false
    },
    content: {
      type: String,
      required: true
    },
    options: {
      type: Array,
      default: function () {
        return ['bold', 'italic', 'underline', 'image', 'heading', 'list-item', 'bullet-list', 'ordered-list', 'history', 'placeholder']
      },
      validator: function (value) {
        for (let i = 0; i < value.length; i++) {
          if (['bold', 'italic', 'underline', 'image', 'heading', 'list-item', 'bullet-list', 'ordered-list', 'history', 'placeholder'].indexOf(value[i]) === -1) {
            return false
          }
        }

        return true
      }
    }
  },
  mounted () {
    if (this.content) {
      this.$refs.editor.setContent(this.content)
    }
  },
  created () {
    EventBus.$on('editor:refresh', this.refreshContent)
  },
  beforeDestroy () {
    EventBus.$off('editor:refresh', this.refreshContent)
  },
  data () {
    return {
      linkMenuIsActive: false,
      linkUrl: null
    }
  },
  computed: {
    extensions: function () {
      let output = [
        new HardBreakNode(),
        new LinkMark()
      ]

      if (this.options.indexOf('bold') !== -1) {
        output.push(new BoldMark())
      }

      if (this.options.indexOf('italic') !== -1) {
        output.push(new ItalicMark())
      }

      if (this.options.indexOf('underline') !== -1) {
        output.push(new UnderlineMark())
      }

      if (this.options.indexOf('image') !== -1) {
        output.push(new ImageNode())
      }

      if (this.options.indexOf('heading') !== -1) {
        output.push(new HeadingNode({ maxLevel: 3 }))
      }

      if (this.options.indexOf('list-item') !== -1) {
        output.push(new ListItemNode())
      }

      if (this.options.indexOf('bullet-list') !== -1) {
        output.push(new BulletListNode())
      }

      if (this.options.indexOf('ordered-list') !== -1) {
        output.push(new OrderedListNode())
      }

      if (this.options.indexOf('history') !== -1) {
        output.push(new HistoryExtension())
      }

      if (this.options.indexOf('placeholder') !== -1) {
        output.push(new PlaceholderExtension())
      }

      return output
    }
  },
  methods: {
    showLinkMenu (attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    refreshContent () {
      console.log('refresh content')
      setTimeout(() => {
        this.$refs.editor.setContent(this.content)
      }, 200)
    },

    hideLinkMenu () {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },

    setLinkUrl (command, url) {
      command({ href: url })
      this.hideLinkMenu()
      this.$refs.editor.focus()
    },

    onUpdate ({ getHTML }) {
      let html = getHTML()

      // Allows 'required' validation rules to work as expected
      if (/^<p>\s*<\/p>$/i.test(html)) {
        html = ''
      }

      this.$emit('update', html)
    }
  }
}
</script>

<style lang="scss">
.editor p.is-empty:first-child::before {
  @apply text-grey;
  content: 'Your content here...';
  float: left;
  pointer-events: none;
  height: 0;
}
</style>
